import { useState, useEffect } from "react";
import { TextInput, NewNativeSelectInput } from "../ui/Input";
import { fetchAllUsers } from "../../services/apiService/userApi";
import debounce from "lodash.debounce";
import useToaster from "../../hooks/useToaster";
import { MdClose } from "react-icons/md";
import { useSelector } from "react-redux";

const permissionOptions = ["View", "Edit", "None"];

const AddUserForm = ({
  userdata,
  oncancel,
  onSave,
  onchange,
  formTitle,
  teams,
}) => {
  const [emailError, setEmailError] = useState("");
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const showToaster = useToaster();
  let [isSubmit, setIsSubmit] = useState(true);
  const { userData } = useSelector((state) => state.user);

  // Track touched state for each field
  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
    email: false,
    team: false,
    articleTaskManagement: false,
    issueTaskManagement: false,
    issueOverview: false,
    articleOverview: false,
    settings: false,
  });

  // Unified error state
  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    team: "",
    articleTaskManagement: "",
    issueTaskManagement: "",
    issueOverview: "",
    articleOverview: "",
    settings: "",
  });

  useEffect(() => {
    let isErrors = Object.values(errors).some((value) => value !== "");
    setIsSubmit(!isErrors);
  }, [errors]);

  // Email validation effect
  useEffect(() => {
    if (formTitle === "User Activation") return;

    const checkEmail = debounce(async (email) => {
      if (!email) return;

      try {
        const response = await fetchAllUsers(userData?.id);
        const users = response?.data;

        if (!Array.isArray(users)) {
          console.error("Users data is not an array:", users);
          return;
        }

        const emailExists = users.some(
          (user) => user?.email?.toLowerCase?.() === email?.toLowerCase?.()
        );
        setEmailError(emailExists ? "Email already exists." : "");
        setErrors((prev) => ({
          ...prev,
          email: emailExists ? "Email already exists." : "",
        }));
      } catch (error) {
        console.error("Error checking email:", error);
        setEmailError("An error occurred while checking the email.");
      } finally {
        setIsCheckingEmail(false);
      }
    }, 500);

    checkEmail(userdata.email);
    return () => checkEmail.cancel();
  }, [userdata.email, formTitle]);

  // Field validation
  const validateField = (name, value) => {
    let error = "";
    if (!value || (typeof value === "string" && value.trim() === "")) {
      error = "This field is required";
    } else if (name === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        error = "Please enter a valid email address";
      }
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
    return error;
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    onchange(e);
    validateField(name, value);
  };

  // Handle blur event
  const handleBlur = (e) => {
    const { name } = e.target;
    // Mark field as touched
    setTouched((prev) => ({ ...prev, [name]: true }));
    // Validate field
    validateField(name, userdata[name]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!navigator.onLine) {
      showToaster("You are currently offline. Please check your internet connection!");
      return;
    }

    const requiredFields = [
      { key: "firstName", label: "First Name" },
      { key: "lastName", label: "Last Name" },
      { key: "email", label: "Email Address" },
      { key: "team", label: "Team" },
      { key: "articleTaskManagement", label: "Article Task Management" },
      { key: "issueTaskManagement", label: "Issue Task Management" },
      { key: "issueOverview", label: "Issue Overview" },
      { key: "articleOverview", label: "Journal Overview" },
      { key: "settings", label: "Settings" },
    ];

    const trimmedData = Object.fromEntries(
      Object.entries(userdata).map(([key, value]) => [
        key,
        typeof value === "string" ? value.trim() : value,
      ])
    );

    let hasErrors = false;

    const newTouched = { ...touched };
    requiredFields.forEach(({ key }) => {
      const fieldError = validateField(key, trimmedData[key]);
      if (fieldError) {
        hasErrors = true;
        newTouched[key] = true;
      }
    });

    setTouched(newTouched);

    if (hasErrors) {
      showToaster("Please fill in all required fields correctly");
      return;
    }

    if (formTitle !== "User Activation" && (emailError || isCheckingEmail)) {
      showToaster(
        "Email Id Already Taken. Please Give A New Email Before Submitting."
      );
      return;
    }

    onSave(e, trimmedData);
  };

  const getError = (name) => {
    return touched[name] ? errors[name] : "";
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
      <div className="bg-white px-6 py-4 rounded-sm shadow-xl min-w-[75%] max-w-5xl max-h-[95vh] overflow-y-auto">
        <div className="flex justify-between items-center h-[64px] border-b mb-4">
          <h2 className="text-lg font-semibold text-[22px] h-[24px] text-[#35424D]">
            {formTitle}
          </h2>
          <button
            className="text-[#35424D] w-[16px] h-[16px]"
            onClick={oncancel}
            aria-label="Close"
          >
            <MdClose />
          </button>
        </div>
        <h2 className="text-[18px] font-semibold text-[#5D6871] h-[24px] mb-4">
          User Information
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <TextInput
              label="First name"
              name="firstName"
              value={userdata.firstName}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="Enter First Name"
              important={true}
              maxLength={100}
              error={getError("firstName")}
              disabled={formTitle === "User Activation" ? true : false}
            />
            <TextInput
              label="Last name"
              name="lastName"
              value={userdata.lastName}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="Enter Last Name"
              important={true}
              maxLength={100}
              error={getError("lastName")}
              disabled={formTitle === "User Activation" ? true : false}
            />
            <TextInput
              label="Email address"
              name="email"
              disabled={formTitle === "User Activation" ? true : false}
              value={userdata.email}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="Enter Email Address"
              important={true}
              maxLength={200}
              error={
                formTitle === "User Activation"
                  ? ""
                  : touched.email || errors["email"]
                  ? errors["email"] || getError("email") || emailError
                  : ""
              }
            />
            {formTitle !== "User Activation" && isCheckingEmail && (
              <p className="text-gray-500 text-sm">Checking email...</p>
            )}
            <NewNativeSelectInput
              label="Team"
              name="team"
              value={userdata.team}
              onChange={handleInputChange}
              onBlur={handleBlur}
              options={teams}
              placeholder="Select Team"
              important={true}
              error={getError("team")}
              disabled={formTitle === "User Activation" ? true : false}
            />
          </div>

          <h2 className="text-[18px] font-semibold text-[#5D6871] h-[24px] mb-4">
            User Permissions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <NewNativeSelectInput
              label="Article Assignment"
              name="articleTaskManagement"
              value={userdata.articleTaskManagement}
              onChange={handleInputChange}
              onBlur={handleBlur}
              options={permissionOptions}
              placeholder="Select Access Type"
              important={true}
              error={getError("articleTaskManagement")}
              disabled={formTitle === "User Activation" ? true : false}
            />
            <NewNativeSelectInput
              label="Issue Task Assignment"
              name="issueTaskManagement"
              value={userdata.issueTaskManagement}
              onChange={handleInputChange}
              onBlur={handleBlur}
              options={permissionOptions}
              placeholder="Select Access Type"
              important={true}
              error={getError("issueTaskManagement")}
              disabled={formTitle === "User Activation" ? true : false}
            />
            <NewNativeSelectInput
              label="Issue Overview"
              name="issueOverview"
              value={userdata.issueOverview}
              onChange={handleInputChange}
              onBlur={handleBlur}
              options={permissionOptions}
              placeholder="Select Access Type"
              important={true}
              error={getError("issueOverview")}
              disabled={formTitle === "User Activation" ? true : false}
            />
            <NewNativeSelectInput
              label="Journal Overview"
              name="articleOverview"
              value={userdata.articleOverview}
              onChange={handleInputChange}
              onBlur={handleBlur}
              options={permissionOptions}
              placeholder="Select Access Type"
              important={true}
              error={getError("articleOverview")}
              disabled={formTitle === "User Activation" ? true : false}
            />
            <NewNativeSelectInput
              label="Settings"
              name="settings"
              value={userdata.settings}
              onChange={handleInputChange}
              onBlur={handleBlur}
              options={permissionOptions}
              placeholder="Select Access Type"
              important={true}
              error={getError("settings")}
              disabled={formTitle === "User Activation" ? true : false}
            />
          </div>

          <div className="flex justify-end border-t pt-4 mb-2">
            <button
              type="submit"
              className={`ml-2 px-4 py-2 rounded-md ${
                isSubmit ? "bg-[#1C40C9]" : "opacity-50 bg-gray-400"
              }  text-white`}
              disabled={!isSubmit}
            >
              {formTitle === "User Activation"
                ? "Activate User"
                : "Create User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export { AddUserForm };
